import React, { useEffect, useState, useContext, useRef } from "react";
import { Button, Modal, InputGroup } from "react-bootstrap";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { Formik } from "formik";
import cf from '../../../services/codeformatter';
import { Form, Row, Col } from "react-bootstrap";
import { useIntl } from "react-intl";
import { FlitServiceContext } from "../../../services/flitService";
import { INVOICE_STATUS, CURRENCY_CODE, INVOICE_ENTRY_TYPE } from "../../../services/flitEnum";
import { getStatusCssClasses } from "../InvoiceUIHelper";
import { useLang } from "../../../../_metronic/i18n/Metronici18n";
import DatePicker from "react-datepicker";
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import FeeEntry from '../../Fee/fee-entry/FeeEntry';
import InvoiceEntry from './InvoiceEntry';

const PER_PAGE = 5;

export function ManualInvoiceDialog({ show, onHide }) {
  const intl = useIntl();
  const lang = useLang();
  const { flitSvc } = useContext(FlitServiceContext);
  const { user } = useSelector(state => state.auth);
  const [billedToOptions, setBilledToOptions] = useState([]);
  const [feeEntries, setFeeEntries] = useState([]);
  const [invoiceEntries, setInvoiceEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const syncGridDataFunc = useRef();
  const [errorMsg, setErrorMsg] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);

  // AsyncTypeahead states
  const [businessEntitySearchCount, setBusinessEntitySearchCount] = useState(0);
  const [businessEntitySearchString, setBusinessEntitySearchString] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedBusinessEntity, setSelectedBusinessEntity] = useState(null);

  const initialEntry = {
    id: "",
    status: INVOICE_STATUS.NEW,
    referenceNo: "",
    billedToId: "",
    notes: "",
    issuedOn: new Date(),
    dueOn: "",
    paidOn: "",
    currency: lang === 'ko' ? CURRENCY_CODE.KRW : lang === 'zh' ? CURRENCY_CODE.CNY : CURRENCY_CODE.USD
  };

  const [initialValues, setInitialValues] = useState(initialEntry);

  useEffect(() => {
    if (show) {
      // Reset form
      setInitialValues(initialEntry);
      setFeeEntries([]);
      setInvoiceEntries([]);
      setErrorMsg('');
      setSelectedBusinessEntity(null);
      setBilledToOptions([]);
      setBusinessEntitySearchString("");
    }
  }, [show]);

  function handleFeeEntriesChange(entries) {
    setFeeEntries(entries);
  }

  function handleInvoiceEntriesChange(entries) {
    setInvoiceEntries(entries);
  }

  function _handleBusinessEntitySearch(name) {
    setBusinessEntitySearchString(name);
    setSearchLoading(true);
    
    flitSvc.searchBusinessEntityIdByName(name, PER_PAGE, 0)
      .then(({ data }) => {
        setBilledToOptions(data.value);
        setBusinessEntitySearchCount(data['@odata.count'] || 0);
      })
      .catch(err => {
        console.error("Error searching business entities:", err);
        setBilledToOptions([]);
      })
      .finally(() => {
        setSearchLoading(false);
      });
  }

  function _filterBusinessEntitySearchCallback(option, props) {
    return true;
  }

  function _handleBusinessEntitySearchPagination(e, shownResults) {
    if (
      billedToOptions.length >= shownResults ||
      billedToOptions.length >= businessEntitySearchCount
    ) {
      return;
    }

    setSearchLoading(true);
    flitSvc.searchBusinessEntityIdByName(
      businessEntitySearchString, 
      shownResults - billedToOptions.length + 1, 
      billedToOptions.length
    )
      .then(({ data }) => {
        setBilledToOptions(billedToOptions.concat(data.value));
        setBusinessEntitySearchCount(data['@odata.count'] || 0);
      })
      .catch(err => {
        console.error("Error loading more results:", err);
      })
      .finally(() => {
        setSearchLoading(false);
      });
  }

  const InvoiceScheme = Yup.object().shape({
    billedToId: Yup.string().required(
      intl.formatMessage({
        id: "AUTH.VALIDATION.REQUIRED_FIELD",
      })
    ),
    dueOn: Yup.string().required(
      intl.formatMessage({
        id: "AUTH.VALIDATION.REQUIRED_FIELD",
      })
    ),
    issuedOn: Yup.string().required(
      intl.formatMessage({
        id: "AUTH.VALIDATION.REQUIRED_FIELD",
      })
    ),
  });

  return (
    <>
      <Modal
        size="xl"
        show={show}
        onHide={onHide}
        aria-labelledby="manual-invoice-dialog"
        centered={true}
      >
        <Formik
          enableReinitialize={true}
          validationSchema={InvoiceScheme}
          initialValues={initialValues}
          validateOnChange={true}
          onSubmit={(values, { setStatus, setSubmitting }) => {
            setSubmitting(true);

            // Get fee entries from the grid
            const entries = syncGridDataFunc.current ? syncGridDataFunc.current() : [];
            
            if (!entries && feeEntries.length === 0 && invoiceEntries.length === 0) {
              setErrorMsg(intl.formatMessage({ id: "INVOICE_ENTRIES_REQUIRED" }));
              setShowErrorModal(true);
              setSubmitting(false);
              return;
            }

            values.feeEntry = entries || feeEntries;
            values.invoiceEntry = invoiceEntries;
            values.status = values.paidOn ? INVOICE_STATUS.PAID : INVOICE_STATUS.OUTSTANDING;

            flitSvc
              .createInvoice(values)
              .then(({ data }) => {
                onHide(true);
              })
              .catch(err => {
                setErrorMsg(err.message || intl.formatMessage({ id: "ERROR_CREATING_INVOICE" }));
                setShowErrorModal(true);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({
            values,
            status,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            validateField,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <>
              <Form onSubmit={handleSubmit} noValidate autoComplete="off" className="form form-label-right">
                <Modal.Header closeButton>
                  <Modal.Title id="manual-invoice-dialog" style={{ width: "60%" }}>
                    {intl.formatMessage({ id: "CREATE_MANUAL_INVOICE" })}
                  </Modal.Title>
                  <InputGroup style={{ display: "block" }}>
                    <span style={{ marginRight: "20px", float: "right" }} className={getStatusCssClasses(values.status)}>
                      {cf.format('InvoiceStatus', values.status, intl.locale)}
                    </span>
                  </InputGroup>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="referenceNo">
                        <Form.Label>{intl.formatMessage({ id: "REFERENCE_NO" })}</Form.Label>
                        <Form.Control 
                          type="text" 
                          name="referenceNo" 
                          value={values.referenceNo}
                          placeholder={intl.formatMessage({ id: "REFERENCE_NO_DESC" })}
                          className={touched.referenceNo && errors.referenceNo ? "is-invalid" : null}
                          onBlur={handleBlur}
                          onChange={handleChange} 
                        />
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block">
                            {errors.referenceNo && touched.referenceNo ? (
                              <div className="fv-help-block">{errors.referenceNo}</div>
                            ) : null}
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="billedToId">
                        <Form.Label>
                          {intl.formatMessage({ id: "BILL_TO" })} *
                        </Form.Label>
                        <AsyncTypeahead
                          id="billedToSearch"
                          onChange={(selected) => {
                            if (selected.length > 0) {
                              setSelectedBusinessEntity(selected[0]);
                              setFieldValue('billedToId', selected[0].id);
                            } else {
                              setSelectedBusinessEntity(null);
                              setFieldValue('billedToId', '');
                            }
                          }}
                          onInputChange={(text, event) => {
                            if (!text) _handleBusinessEntitySearch('');
                          }}
                          selected={selectedBusinessEntity ? [selectedBusinessEntity] : []}
                          paginate
                          maxResults={PER_PAGE - 1}
                          isLoading={searchLoading}
                          multiple={false}
                          allowNew={false}
                          options={billedToOptions}
                          labelKey={(option) => `${option.legalName}`}
                          minLength={0}
                          onSearch={_handleBusinessEntitySearch}
                          filterBy={_filterBusinessEntitySearchCallback}
                          onPaginate={_handleBusinessEntitySearchPagination}
                          placeholder={intl.formatMessage({ id: "SELECT_BUSINESS_ENTITY" })}
                          renderMenuItemChildren={(option, props) => (
                            <div key={option.id}>
                              <strong>{`${option.legalName}${option.localizedLegalName ? ` (${option.localizedLegalName})` : ''}`}</strong>
                            </div>
                          )}
                          paginationText={intl.formatMessage({ id: "SHOW_MORE_RESULTS" })}
                          useCache={false}
                          onBlur={() => setFieldTouched('billedToId', true)}
                          isInvalid={touched.billedToId && errors.billedToId}
                        />
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block">
                            {errors.billedToId && touched.billedToId ? (
                              <div className="fv-help-block">{errors.billedToId}</div>
                            ) : null}
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group controlId="currency">
                        <Form.Label>
                          {intl.formatMessage({ id: "CURRENCY" })}
                        </Form.Label>
                        <Form.Control
                          as="select"
                          name="currency"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.currency}
                        >
                          <option value={CURRENCY_CODE.USD}>{CURRENCY_CODE.USD}</option>
                          <option value={CURRENCY_CODE.KRW}>{CURRENCY_CODE.KRW}</option>
                          <option value={CURRENCY_CODE.CNY}>{CURRENCY_CODE.CNY}</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group controlId="issuedOn">
                        <Form.Label>{intl.formatMessage({ id: "ISSUED_ON" })}</Form.Label><br />
                        <DatePicker 
                          name="issuedOn" 
                          selected={values.issuedOn} 
                          onChange={date => {
                            setFieldValue('issuedOn', date);
                          }}
                          placeholderText={intl.formatMessage({ id: "SELECT_DATE" })}
                          className='form-control' 
                          onBlur={handleBlur}
                        />
                        <div className="fv-plugins-message-container">
                          {errors.issuedOn && touched.issuedOn ? (
                            <div className="fv-help-block">{errors.issuedOn}</div>
                          ) : null}
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="dueOn">
                        <Form.Label>{intl.formatMessage({ id: "DUE_ON" })}</Form.Label><br />
                        <DatePicker 
                          name="dueOn" 
                          selected={values.dueOn} 
                          onChange={date => {
                            setFieldValue('dueOn', date);
                          }}
                          placeholderText={intl.formatMessage({ id: "SELECT_DATE" })}
                          className='form-control' 
                          onBlur={handleBlur}
                        />
                        <div className="fv-plugins-message-container">
                          {errors.dueOn && touched.dueOn ? (
                            <div className="fv-help-block">{errors.dueOn}</div>
                          ) : null}
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="paidOn">
                        <Form.Label>{intl.formatMessage({ id: "PAID_ON" })}</Form.Label><br />
                        <DatePicker 
                          name="paidOn" 
                          selected={values.paidOn} 
                          onChange={date => {
                            setFieldValue('paidOn', date);
                          }}
                          placeholderText={intl.formatMessage({ id: "SELECT_DATE" })}
                          className='form-control' 
                          onBlur={handleBlur}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Form.Group controlId="notes">
                        <Form.Label>{intl.formatMessage({ id: "NOTES" })}</Form.Label>
                        <Form.Control 
                          as="textarea" 
                          rows="3" 
                          name="notes" 
                          value={values.notes} 
                          disabled={isSubmitting}
                          className={touched.notes && errors.notes ? "is-invalid" : null}
                          onBlur={handleBlur}
                          onChange={handleChange} 
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />

                  {/* Fee Entry Section */}
                  <Row>
                    <Col>
                      <h5>{intl.formatMessage({ id: "FEE_ENTRIES" })}</h5>
                      <FeeEntry 
                        syncGridDataFunc={syncGridDataFunc} 
                        initialFeeEntries={[]} 
                        feeEntriesChanged={handleFeeEntriesChange}
                      />
                    </Col>
                  </Row>

                  <hr />

                  {/* Invoice Entry Section */}
                  <Row>
                    <Col>
                      <h5>{intl.formatMessage({ id: "ADDITIONAL_ENTRIES" })}</h5>
                      <InvoiceEntry 
                        initialInvoiceEntries={[]} 
                        invoiceEntriesChanged={handleInvoiceEntriesChange}
                      />
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  {Object.keys(errors).length !== 0 ? (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">{intl.formatMessage({ id: "FORM_ERROR" })}</div>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onHide()}
                    className="btn btn-light btn-elevate"
                  >
                    {intl.formatMessage({ id: "CANCEL_BUTTON" })}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || isLoading}
                    className="btn btn-primary btn-elevate"
                  >
                    {intl.formatMessage({ id: "CREATE_INVOICE" })}
                    {(isSubmitting || isLoading) && <span className="ml-3 mr-5 spinner spinner-white"></span>}
                  </button>
                </Modal.Footer>
              </Form>
            </>
          )}
        </Formik>
      </Modal>

      {/* Error Modal */}
      <Modal
        size="md"
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        aria-labelledby="error-modal"
        centered={true}
      >
        <Modal.Header closeButton>
          <Modal.Title id="error-modal">{intl.formatMessage({ id: "ACTION_ERROR_TITLE" })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMsg}</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            onClick={() => setShowErrorModal(false)}
            className="btn btn-danger btn-elevate"
          >
            {intl.formatMessage({ id: "CLOSE_BUTTON" })}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}